import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsClockwise, Palette } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY_SWATCHES = ['#111111', '#555555', '#999999', '#CCCCCC', '#F2F2F2'];
const emptySlots = () => Array.from({ length: 5 }, () => ({ color: '', source: 'empty', locked: false }));

export default function DesignV3ProjectPalette({ api, projectId, editorRootRef, assetRevision = 0 }) {
  const [mountNode, setMountNode] = useState(null);
  const [slots, setSlots] = useState(emptySlots);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/design/v3/palette`);
      const next = Array.isArray(data?.slots) ? data.slots.slice(0, 5) : [];
      setSlots([...next, ...emptySlots()].slice(0, 5));
    } catch (error) {
      setSlots(emptySlots());
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load, assetRevision]);

  useEffect(() => {
    const root = editorRootRef?.current;
    if (!root) return undefined;
    let active = true;
    const sync = () => {
      if (!active) return;
      const toolbar = root.querySelector('[data-testid="v3-editor-utility-toolbar"]');
      if (!toolbar) {
        setMountNode(null);
        return;
      }
      let host = toolbar.querySelector('[data-v3-project-palette-host="true"]');
      if (!host) {
        host = document.createElement('div');
        host.dataset.v3ProjectPaletteHost = 'true';
        host.className = 'contents';
        toolbar.appendChild(host);
      }
      setMountNode(current => current === host ? current : host);
    };
    sync();
    const timer = window.setInterval(sync, 180);
    return () => {
      active = false;
      window.clearInterval(timer);
      root.querySelector('[data-v3-project-palette-host="true"]')?.remove();
    };
  }, [editorRootRef]);

  const saveSlot = async (index, color) => {
    const normalized = String(color || '').toUpperCase();
    const next = slots.map((slot, slotIndex) => slotIndex === index
      ? { color: normalized, source: 'manual', locked: true }
      : slot);
    setSlots(next);
    try {
      const { data } = await api.put(`/projects/${projectId}/design/v3/palette`, { slots: next });
      setSlots([...(data?.slots || []), ...emptySlots()].slice(0, 5));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Palette DNA non salvata');
      await load();
    }
  };

  const recalculate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/design/v3/palette/recalculate`);
      setSlots([...(data?.slots || []), ...emptySlots()].slice(0, 5));
      toast.success('Palette ricalcolata dalle reference');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile ricalcolare la palette');
    } finally {
      setBusy(false);
    }
  };

  if (!mountNode) return null;

  return createPortal(
    <>
      <span className="h-6 w-px bg-[var(--border-color)] mx-1" aria-hidden="true"/>
      <div className="flex h-9 items-center gap-1.5 rounded-lg border border-[var(--border-color)] bg-black/10 px-2" data-testid="v3-project-palette" title="Palette Design DNA del progetto">
        <Palette size={15} className="shrink-0 text-[var(--text-muted)]" aria-hidden="true"/>
        <span className="hidden 2xl:inline text-[9px] uppercase tracking-[.12em] text-[var(--text-muted)]">Palette</span>
        <div className="flex items-center gap-[3px]" data-testid="v3-project-palette-swatches">
          {slots.map((slot, index) => {
            const hasColor = /^#[0-9A-F]{6}$/.test(String(slot?.color || '').toUpperCase());
            const display = hasColor ? slot.color : EMPTY_SWATCHES[index];
            const sourceLabel = slot?.source === 'manual' ? 'manuale' : slot?.source === 'auto' ? 'estratto dalle reference' : 'vuoto';
            return <label
              key={index}
              className={`relative h-6 w-6 overflow-hidden rounded-[3px] border cursor-pointer transition-transform hover:scale-110 ${hasColor ? 'border-white/25' : 'border-white/10 opacity-45'}`}
              style={{ backgroundColor: display }}
              title={`Colore ${index + 1}: ${hasColor ? slot.color : 'vuoto'} · ${sourceLabel}. Clicca per modificare.`}
              data-testid={`v3-project-palette-swatch-${index}`}
            >
              {!hasColor && <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_46%,rgba(255,255,255,.35)_47%,rgba(255,255,255,.35)_53%,transparent_54%)]" aria-hidden="true"/>}
              {slot?.source === 'manual' && <span className="absolute bottom-0 right-0 h-1.5 w-1.5 rounded-tl-sm bg-fuchsia-400" aria-hidden="true"/>}
              <input
                type="color"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                value={display}
                onChange={event => saveSlot(index, event.target.value)}
                aria-label={`Modifica colore palette ${index + 1}`}
              />
            </label>;
          })}
        </div>
        <button
          type="button"
          className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--text-muted)] hover:bg-white/5 hover:text-fuchsia-300 disabled:opacity-40"
          onClick={recalculate}
          disabled={busy}
          title="Ricalcola dalle reference"
          aria-label="Ricalcola palette dalle reference"
          data-testid="v3-project-palette-recalculate"
        >
          <ArrowsClockwise size={14} className={busy ? 'animate-spin' : ''}/>
        </button>
      </div>
    </>,
    mountNode,
  );
}
